import { FC, useState } from 'react'
import { Popover } from '../Atoms/Popover'
import {
  logout,
  setClient,
  setOriginBusinessUnits,
  setPaymentMethod,
} from '../../store/slices'
import { useNavigate } from 'react-router-dom'
import logo from '../../assets/images/TEALCA-logo.svg'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  KeyIcon,
  UserIcon,
  ArrowLeftOnRectangleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import {
  AccountInterface,
  PaymentMethodAccountDto,
  PaymentModeId,
} from '../../interfaces'
import { AccountChangeClientModal } from '../Account/AccountChangeClientModal'
import {
  alertService,
  getAccount,
  getBusinessUnits,
  getOriginBusinessUnits,
  GetPaymentMethodAccount,
} from '../../services'
import Greeting from './Greeting'
import { resolveAgreementIDForSite } from '../../utils/accountSiteAgreement'
import { invalidateFavoriteDestinationsCache } from '../../utils/favoriteDestinationsCache'

const MenuOpenIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="2.6" rx="1.3" />
    <rect x="10" y="10.7" width="11" height="2.6" rx="1.3" />
    <rect x="3" y="17.4" width="18" height="2.6" rx="1.3" />
    <path d="M8 8.4 3.3 12 8 15.6z" />
  </svg>
)

const MenuClosedIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="2.6" rx="1.3" />
    <rect x="3" y="10.7" width="11" height="2.6" rx="1.3" />
    <rect x="3" y="17.4" width="18" height="2.6" rx="1.3" />
    <path d="M16 8.4 20.7 12 16 15.6z" />
  </svg>
)

interface HeaderProps {
  /** Alterna la visibilidad del menú lateral en desktop. */
  onToggleSidebar?: () => void
  sidebarCollapsed?: boolean
}

export const Header: FC<HeaderProps> = ({
  onToggleSidebar,
  sidebarCollapsed,
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [openModal, setOpenModal] = useState(false)
  const user = useAppSelector((state) => state.user)!
  // const notifications = useMemo(
  //   () => ['Notificación 1', 'Notificación 2', 'Notificación 3'],
  //   []
  // );
  // const [errorMessage, setErrorMessage] = useState('');

  async function handleAccountSiteSelected(newAccountSite: AccountInterface) {
    let accountSiteInfo = await getAccount(newAccountSite.id)
    // Mismo criterio que el login: el contrato lo define la línea de facturación
    // seleccionada, no la raíz de la cuenta (varias líneas comparten accountID).
    const agreementID = resolveAgreementIDForSite(
      accountSiteInfo,
      newAccountSite
    )
    const businessUnits = await getBusinessUnits()
    let businessUnitsSelected = businessUnits!.find(
      (t) => t.buCode === newAccountSite?.BuCodeSource
    )!
    let paymentMetod = await GetPaymentMethodAccount(newAccountSite.id)
    if (paymentMetod.model === undefined || paymentMetod.model?.length === 0) {
      alertService.error(
        'Error',
        'Contacte a soporte, el cliente no contiene Modadidad de pago asociado',
        { autoClose: false }
      )
      return
    }
    let paymentMetodFitler = paymentMetod.model?.filter(
      (x) =>
        x.paymentModeID == PaymentModeId.COD ||
        x.paymentModeID == PaymentModeId.CONTADO ||
        (newAccountSite.accountSiteID !== 0 &&
          x.paymentModeID == PaymentModeId.CREDIT)
    )

    newAccountSite = {
      ...newAccountSite,
      businessUnit: businessUnitsSelected,
      agreementID,
      abbreviationName: accountSiteInfo?.abbreviationName ?? '',
      identificationNumber: accountSiteInfo?.identificationNumber ?? '',
      listAccountPhone: accountSiteInfo?.listAccountPhone ?? [],
      listAccountEmail: accountSiteInfo?.listAccountEmail ?? [],
      listAuthorizingAccountClient:
        accountSiteInfo?.listAuthorizingAccount ?? [],
      fiscalAddress: accountSiteInfo?.fiscalAddress,
      accountSiteName: newAccountSite.accountFullName,
      accountFullName:
        accountSiteInfo?.accountFullName ?? newAccountSite.accountFullName,
    }

    dispatch(
      setClient({
        ...newAccountSite,
        listAuthorizingAccount: user.client
          ?.listAuthorizingAccount as AccountInterface[],
      })
    )
    dispatch(setPaymentMethod(paymentMetodFitler as PaymentMethodAccountDto[]))

    const originBusinessUnits = await getOriginBusinessUnits(agreementID)
    dispatch(setOriginBusinessUnits(originBusinessUnits))

    setOpenModal(false)
    navigate('/dashboard')
  }

  // const handlePrintLabel = async () => {
  //   const printSucceed = await PrintLabel(4759124, 60, '1120B', [1]);
  //   if (!!printSucceed) {
  //     alert('La etiqueta se imprimió correctamente.');
  //   }
  // };

  // const NotificationButton = useMemo(() => {
  //   return (
  //     <div>
  //       <BellIcon className="w-7 h-7 text-gray-500" />

  //       {notifications.length > 0 && (
  //         <div className="absolute bottom-0 -right-1 h-4 w-4 bg-main-500 rounded-full text-white text-xs flex items-center justify-center">
  //           {notifications.length > 9 ? '9+' : notifications.length}
  //         </div>
  //       )}
  //     </div>
  //   );
  // }, [notifications]);

  return (
    <div className="flex flex-1 py-3 px-2 sm:px-6 justify-between border fixed lg:static bg-gray-50 z-20 w-full">
      {/* Logo + toggle del menú */}
      <div className="flex items-center gap-1 sm:gap-2">
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            title={sidebarCollapsed ? 'Mostrar menú' : 'Ocultar menú'}
            aria-label={sidebarCollapsed ? 'Mostrar menú' : 'Ocultar menú'}
            aria-expanded={!sidebarCollapsed}
            className="hidden lg:inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-200 hover:text-gray-700"
          >
            {sidebarCollapsed ? (
              <MenuClosedIcon className="h-6 w-6" />
            ) : (
              <MenuOpenIcon className="h-6 w-6" />
            )}
          </button>
        )}
        <div
          onClick={() => navigate('/dashboard')}
          className="flex justify-center cursor-pointer"
        >
          <img src={logo} alt="Tealca logo" width="75" height="75" />
        </div>
      </div>

      {/* User data: columna de texto alineada a la derecha + icono a la derecha */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-end gap-2">
          {/* Texto a la IZQUIERDA del icono, alineado a la derecha */}
          <div className="flex flex-col gap-1 items-end text-right">
            <span className="text-sm text-gray-700 truncate">
              <Greeting />
              <b>
                {user.user?.name} {user.user?.lastName}
              </b>
            </span>

            <div className="flex gap-2 text-sm justify-end">
              <span className="text-gray-700">Cliente:</span>
              <span className="text-gray-900 font-semibold">
                {user.client?.accountFullName}
              </span>
            </div>
          </div>

          {/* Icono a la DERECHA */}
          <div className="cursor-pointer">
            <Popover
              button={<UserIcon className="w-7 h-7 text-gray-500" />}
              containerClassname="!-mt-1 ml-[120px] z-40"
            >
              <div className="flex flex-col">
                <div
                  onClick={() => {
                    // La libreta son datos de terceros y el equipo comparte
                    // equipos: se vacía entera antes de cerrar sesión.
                    invalidateFavoriteDestinationsCache()
                    dispatch(logout())
                  }}
                  className="flex flex-1 flex-col text-sm text-gray-700 truncate hover:bg-main-500 hover:text-white"
                >
                  <div className="flex gap-2 py-2 px-4">
                    <ArrowLeftOnRectangleIcon className="w-5 h-5" />
                    <span>Cerrar Sesión</span>
                  </div>

                  <hr className="w-full text-gray-400" />
                </div>

                <div
                  onClick={() => navigate('/reset-password')}
                  className="flex flex-1 flex-col text-sm text-gray-700 truncate hover:bg-main-500 hover:text-white"
                >
                  <div className="flex gap-2 py-2 px-4">
                    <KeyIcon className="w-5 h-5" />
                    <span>Cambiar Contraseña</span>
                  </div>
                </div>

                {(user.client?.listAuthorizingAccount.length ?? 1) > 1 && (
                  <div
                    onClick={() => setOpenModal(true)}
                    className="flex flex-1 flex-col text-sm text-gray-700 truncate hover:bg-main-500 hover:text-white"
                  >
                    <div className="flex gap-2 py-2 px-4">
                      <UsersIcon className="w-5 h-5" />
                      <span>Cambiar Cliente</span>
                    </div>
                  </div>
                )}
              </div>
            </Popover>
          </div>
        </div>

        {/* Linea de Facturación: debajo del icono, alineada a la derecha */}
        <div className="flex gap-2 text-sm justify-end">
          <span className="text-gray-700">Linea de Facturación:</span>
          <span className="text-gray-900 font-semibold">
            {user.client?.accountShortName !== undefined &&
            user.client?.accountShortName !== null
              ? user.client?.accountShortName + ' -'
              : ''}{' '}
            {user.client?.accountSiteName}
          </span>
        </div>

        {!!openModal && (
          <AccountChangeClientModal
            accounList={
              user.client?.listAuthorizingAccount as AccountInterface[]
            }
            account={user.client as AccountInterface}
            open={openModal}
            setOpen={setOpenModal}
            onSaveChange={handleAccountSiteSelected}
          />
        )}
      </div>
    </div>
  )
}
